import {
    Calendar,
    Event,
    ISODateString,
    ISODateTimeString,
    Todo,
    TodoDailyRecord,
    User
} from './models';

export const OWNIT_WIDGET_SUMMARY_SCHEMA_VERSION = 1;

export type OwnitWidgetSize = 'small' | 'medium' | 'large';
export type OwnitWidgetSummarySource = 'live' | 'cache';

export interface OwnitWidgetSummaryInput {
    user: User;
    date: ISODateString;
    now: Date;
    timezone: string;
    events: Event[];
    todos: Todo[];
    calendars?: Calendar[];
    dailyRecord?: TodoDailyRecord | null;
    source?: OwnitWidgetSummarySource;
    cacheKey?: string;
    cachedAt?: ISODateTimeString | null;
}

export interface OwnitWidgetScheduleItem {
    id: string;
    title: string;
    date: ISODateString;
    startDate: ISODateString;
    endDate: ISODateString;
    timeLabel: string;
    sortAt: ISODateTimeString;
    color: string;
    calendarId: string;
    calendarName: string;
    isAllDay: boolean;
    isShared: boolean;
    completed: boolean;
}

export interface OwnitWidgetTodoItem {
    id: string;
    title: string;
    category: string;
    color: string;
    completed: boolean;
    isRoutine: boolean;
    routineId: string | null;
    memo: string;
}

export interface OwnitWidgetScheduleSummary {
    date: ISODateString;
    totalCount: number;
    completedCount: number;
    openCount: number;
    items: OwnitWidgetScheduleItem[];
}

export interface OwnitWidgetTodoSummary {
    date: ISODateString;
    totalCount: number;
    completedCount: number;
    openCount: number;
    completionRate: number;
    items: OwnitWidgetTodoItem[];
}

export interface OwnitWidgetRoutineSummary {
    date: ISODateString;
    totalCount: number;
    completedCount: number;
    openCount: number;
    completionRate: number;
    items: OwnitWidgetTodoItem[];
}

export interface OwnitWidgetSmallData {
    size: 'small';
    date: ISODateString;
    headline: string;
    completionRate: number;
    nextTitle: string;
    accentColor: string;
}

export interface OwnitWidgetMediumData {
    size: 'medium';
    date: ISODateString;
    title: string;
    completionRate: number;
    scheduleItems: OwnitWidgetScheduleItem[];
    todoItems: OwnitWidgetTodoItem[];
    nextSchedule: OwnitWidgetScheduleItem | null;
}

export interface OwnitWidgetLargeData {
    size: 'large';
    date: ISODateString;
    title: string;
    completionRate: number;
    todaySchedule: OwnitWidgetScheduleSummary;
    todayTodo: OwnitWidgetTodoSummary;
    routineStatus: OwnitWidgetRoutineSummary;
    nextSchedule: OwnitWidgetScheduleItem | null;
}

export interface OwnitWidgetSummary {
    schemaVersion: typeof OWNIT_WIDGET_SUMMARY_SCHEMA_VERSION;
    source: OwnitWidgetSummarySource;
    generatedAt: ISODateTimeString;
    cachedAt: ISODateTimeString | null;
    cacheKey: string;
    staleAfterMinutes: number;
    date: ISODateString;
    dateLabel: string;
    timezone: string;
    user: {
        id: string;
        name: string;
        nickname: string;
    };
    completionRate: number;
    todaySchedule: OwnitWidgetScheduleSummary;
    todayTodo: OwnitWidgetTodoSummary;
    nextSchedule: OwnitWidgetScheduleItem | null;
    routineStatus: OwnitWidgetRoutineSummary;
    widgets: {
        small: OwnitWidgetSmallData;
        medium: OwnitWidgetMediumData;
        large: OwnitWidgetLargeData;
    };
}

export interface OwnitWidgetSummaryCache {
    schemaVersion: typeof OWNIT_WIDGET_SUMMARY_SCHEMA_VERSION;
    userId: string;
    date: ISODateString;
    cachedAt: ISODateTimeString;
    summary: OwnitWidgetSummary;
}

export function createOwnitWidgetSummary(input: OwnitWidgetSummaryInput): OwnitWidgetSummary {
    const now = input.now instanceof Date && !Number.isNaN(input.now.getTime()) ? input.now : new Date();
    const generatedAt = now.toISOString();
    const date = safeDateKey(input.date, now);
    const calendarById = new Map((input.calendars || []).map((calendar) => [calendar.id, calendar]));
    const todayEvents = (input.events || [])
        .filter((event) => isDateWithin(date, event.startDate, event.endDate))
        .map((event) => scheduleItemFromEvent(event, date, calendarById, now))
        .sort(compareScheduleItems);
    const todayTodos = (input.todos || [])
        .filter((todo) => todo.date === date)
        .map(todoItemFromTodo)
        .sort(compareTodoItems);
    const routineItems = todayTodos.filter((todo) => todo.isRoutine);
    const completionRate = input.dailyRecord?.completionRate ?? percent(todayTodos.filter((todo) => todo.completed).length, todayTodos.length);
    const nextSchedule = nextScheduleItem(input.events || [], calendarById, now);
    const todaySchedule = createScheduleSummary(date, todayEvents);
    const todayTodo = createTodoSummary(date, todayTodos, completionRate);
    const routineStatus = createRoutineSummary(date, routineItems);
    const title = `${dateLabel(date)} Ownit`;
    const smallHeadline = todayTodos.length > 0
        ? `${todayTodo.completedCount}/${todayTodo.totalCount} 완료`
        : `${todaySchedule.openCount}개 일정`;
    const fallbackAccent = todayTodos[0]?.color || todayEvents[0]?.color || '#6B6259';
    const summary: OwnitWidgetSummary = {
        schemaVersion: OWNIT_WIDGET_SUMMARY_SCHEMA_VERSION,
        source: input.source || 'live',
        generatedAt,
        cachedAt: input.cachedAt || null,
        cacheKey: input.cacheKey || widgetSummaryCacheKey(input.user.id),
        staleAfterMinutes: 180,
        date,
        dateLabel: dateLabel(date),
        timezone: input.timezone || 'local',
        user: {
            id: input.user.id,
            name: input.user.name || input.user.nickname || 'Ownit',
            nickname: input.user.nickname || input.user.name || 'Ownit'
        },
        completionRate,
        todaySchedule,
        todayTodo,
        nextSchedule,
        routineStatus,
        widgets: {
            small: {
                size: 'small',
                date,
                headline: smallHeadline,
                completionRate,
                nextTitle: nextSchedule?.title || firstOpenTodoTitle(todayTodos) || '오늘 할 일을 정리해 보세요',
                accentColor: nextSchedule?.color || fallbackAccent
            },
            medium: {
                size: 'medium',
                date,
                title,
                completionRate,
                scheduleItems: todayEvents.slice(0, 2),
                todoItems: todayTodos.slice(0, 3),
                nextSchedule
            },
            large: {
                size: 'large',
                date,
                title,
                completionRate,
                todaySchedule: {
                    ...todaySchedule,
                    items: todaySchedule.items.slice(0, 5)
                },
                todayTodo: {
                    ...todayTodo,
                    items: todayTodo.items.slice(0, 6)
                },
                routineStatus: {
                    ...routineStatus,
                    items: routineStatus.items.slice(0, 4)
                },
                nextSchedule
            }
        }
    };
    return summary;
}

export function createOwnitWidgetSummaryCache(summary: OwnitWidgetSummary): OwnitWidgetSummaryCache {
    return {
        schemaVersion: OWNIT_WIDGET_SUMMARY_SCHEMA_VERSION,
        userId: summary.user.id,
        date: summary.date,
        cachedAt: new Date().toISOString(),
        summary: {
            ...summary,
            source: 'cache',
            cachedAt: new Date().toISOString()
        }
    };
}

export function widgetSummaryCacheKey(userId: string): string {
    return `ownit.widget.summary.v1.${userId || 'guest'}`;
}

function createScheduleSummary(date: string, items: OwnitWidgetScheduleItem[]): OwnitWidgetScheduleSummary {
    const completedCount = items.filter((item) => item.completed).length;
    return {
        date,
        totalCount: items.length,
        completedCount,
        openCount: Math.max(0, items.length - completedCount),
        items
    };
}

function createTodoSummary(date: string, items: OwnitWidgetTodoItem[], completionRate: number): OwnitWidgetTodoSummary {
    const completedCount = items.filter((item) => item.completed).length;
    return {
        date,
        totalCount: items.length,
        completedCount,
        openCount: Math.max(0, items.length - completedCount),
        completionRate,
        items
    };
}

function createRoutineSummary(date: string, items: OwnitWidgetTodoItem[]): OwnitWidgetRoutineSummary {
    const completedCount = items.filter((item) => item.completed).length;
    return {
        date,
        totalCount: items.length,
        completedCount,
        openCount: Math.max(0, items.length - completedCount),
        completionRate: percent(completedCount, items.length),
        items
    };
}

function scheduleItemFromEvent(event: Event, date: string, calendarById: Map<string, Calendar>, now: Date): OwnitWidgetScheduleItem {
    const calendar = calendarById.get(event.calendarId) || null;
    return {
        id: event.id,
        title: cleanText(event.title, '제목 없는 일정'),
        date,
        startDate: event.startDate,
        endDate: event.endDate,
        timeLabel: eventTimeLabel(event, date),
        sortAt: eventSortDate(event, date, now).toISOString(),
        color: event.color || calendar?.color || '#6B6259',
        calendarId: event.calendarId,
        calendarName: calendar?.name || (event.visibility === 'group' ? '공유 캘린더' : '개인 캘린더'),
        isAllDay: Boolean(event.allDay),
        isShared: calendar?.type === 'shared' || event.visibility === 'group',
        completed: Boolean(event.completed)
    };
}

function todoItemFromTodo(todo: Todo): OwnitWidgetTodoItem {
    return {
        id: todo.id,
        title: cleanText(todo.title, '제목 없는 Todo'),
        category: cleanText(todo.category, todo.routineId ? '루틴' : 'Todo'),
        color: todo.color || '#6B6259',
        completed: Boolean(todo.completed),
        isRoutine: Boolean(todo.routineId),
        routineId: todo.routineId || null,
        memo: todo.memo || ''
    };
}

function nextScheduleItem(events: Event[], calendarById: Map<string, Calendar>, now: Date): OwnitWidgetScheduleItem | null {
    const nowMs = now.getTime();
    const candidates = events
        .filter((event) => !event.completed)
        .map((event) => {
            const start = eventStartDate(event);
            const end = eventEndDate(event);
            const isActive = start.getTime() <= nowMs && end.getTime() >= nowMs;
            return {
                event,
                rank: isActive ? nowMs - 1 : start.getTime()
            };
        })
        .filter((item) => item.rank >= nowMs - 1)
        .sort((a, b) => a.rank - b.rank || a.event.title.localeCompare(b.event.title));
    const next = candidates[0]?.event || null;
    if (!next) return null;
    return scheduleItemFromEvent(next, safeDateKey(next.startDate, now), calendarById, now);
}

function compareScheduleItems(a: OwnitWidgetScheduleItem, b: OwnitWidgetScheduleItem): number {
    if (a.sortAt !== b.sortAt) return a.sortAt.localeCompare(b.sortAt);
    return a.title.localeCompare(b.title);
}

function compareTodoItems(a: OwnitWidgetTodoItem, b: OwnitWidgetTodoItem): number {
    if (a.completed !== b.completed) return Number(a.completed) - Number(b.completed);
    if (a.isRoutine !== b.isRoutine) return Number(a.isRoutine) - Number(b.isRoutine);
    return a.title.localeCompare(b.title);
}

function eventSortDate(event: Event, date: string, now: Date): Date {
    const start = eventStartDate(event);
    const end = eventEndDate(event);
    if (start.getTime() <= now.getTime() && end.getTime() >= now.getTime()) return now;
    if (date > event.startDate) return new Date(`${date}T00:01:00`);
    return start;
}

function eventStartDate(event: Event): Date {
    return parseLocalDateTime(event.startDate, event.allDay ? '09:00' : event.startTime || '09:00');
}

function eventEndDate(event: Event): Date {
    return parseLocalDateTime(event.endDate || event.startDate, event.allDay ? '23:59' : event.endTime || event.startTime || '23:59');
}

function eventTimeLabel(event: Event, date: string): string {
    if (event.startDate !== event.endDate) {
        if (date === event.startDate) return event.allDay ? '시작일' : `${event.startTime || '09:00'} 시작`;
        if (date === event.endDate) return event.allDay ? '마감일' : `${event.endTime || '23:59'} 종료`;
        return '진행 중';
    }
    if (event.allDay) return '하루 종일';
    if (event.startTime && event.endTime) return `${event.startTime}-${event.endTime}`;
    return event.startTime || '시간 없음';
}

function parseLocalDateTime(date: string, time: string): Date {
    const parsed = new Date(`${date}T${time}:00`);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
}

function isDateWithin(date: string, start: string, end: string): boolean {
    return date >= start && date <= end;
}

function percent(completed: number, total: number): number {
    if (total <= 0) return 0;
    return Math.round((completed / total) * 100);
}

function firstOpenTodoTitle(items: OwnitWidgetTodoItem[]): string {
    return items.find((item) => !item.completed)?.title || '';
}

function safeDateKey(value: string, fallback: Date): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return value;
    const local = new Date(fallback.getTime() - fallback.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
}

function dateLabel(date: string): string {
    const parsed = new Date(`${date}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return date;
    return `${parsed.getMonth() + 1}월 ${parsed.getDate()}일`;
}

function cleanText(value: string | null | undefined, fallback: string): string {
    const text = (value || '').trim();
    return text || fallback;
}
